import {UserState} from "../../redux/reducer/user";
import {CompanyProps} from "../../api/model/company";
import {UserProps} from "../../api/model/user";
import {WindowedAgencyProps} from "../../api/model/agency";

/**
 * Determines if a user holds any administrative role based on user data, agency data, and company data.
 *
 * This function evaluates whether a user has an administrative role in agencies, companies,
 * or if the user is identified as a KlimaLink admin. It returns an object containing boolean
 * values for each administrative role type.
 *
 * @param {UserState["userData"]} userData - Information about the user, containing properties such as `isKlimaLink`.
 * @param {UserState["agencies"]} agenciesData - A list of agency objects associated with the user, each having an `isAdmin` property.
 * @param {UserState["companies"]} companiesData - A list of company objects associated with the user, with the first company's `isAdmin` property being checked.
 * @returns {{isSomeAgencyAdmin: boolean, isCompanyAdmin: boolean, isKlimaLinkAdmin: boolean}} - An object indicating if the user is an admin in any of these categories.
 */
export const isSomeAdmin = (
    userData: UserState["userData"],
    agenciesData: UserState["agencies"] | WindowedAgencyProps[],
    companiesData: UserState["companies"]
): { isSomeAgencyAdmin: boolean; isCompanyAdmin: boolean; isKlimaLinkAdmin: boolean; } => {
    let isSomeAgencyAdmin = false
    let isCompanyAdmin = false
    let isKlimaLinkAdmin = false
    if (agenciesData) isSomeAgencyAdmin = agenciesData.some(agency => agency.isAdmin)
    if (companiesData) isCompanyAdmin = companiesData[0].isAdmin
    if (userData) isKlimaLinkAdmin = userData.isKlimaLink
    return {isSomeAgencyAdmin, isCompanyAdmin, isKlimaLinkAdmin}
}

/**
 * Checks if the company admin has accepted the terms and conditions.
 *
 * This function evaluates whether the current user, when identified as an admin of the company,
 * has accepted the terms and conditions. If the user is not an admin, it defaults to true.
 *
 * @param {CompanyProps[]} [companies] - Array of company objects the user is associated with. Currently, a user is assumed to be in only one company at a time.
 * @param {UserProps} [user] - User details containing information about terms and conditions acceptance.
 * @returns {boolean} - Returns `true` if the company admin has accepted the terms and conditions, or if the user is not an admin. Returns `false` if the admin has not accepted the terms.
 */
export const companyAdminHasAcceptedTermsAndConditions = (companies?: CompanyProps[], user?: UserProps): boolean => {
    if (companies && user) {
        const {isAdmin} = companies[0] // currently a user (unless its KlimaLink Admin) can only be in one company at a time, that is why it is save to simply pick the first company in the array
        if (isAdmin) return user.termsAndConditionsAccepted
        return true
    }
    return false
}

/**
 * This function checks whether a normal user has not accepted the terms and conditions.
 *
 * @function
 * @param {UserProps} [user] - An optional user object containing user details, including the terms and conditions acceptance status.
 * @returns {boolean} Returns `true` if the user has not accepted the terms and conditions or `false` otherwise. If no user object is provided, it defaults to `false`.
 */
export const normalUserHasNotAcceptedTermsAndConditions = (user?: UserProps): boolean => {
    if (user) return user.termsAndConditionsAccepted
    return false
}